import { getAuth, onAuthStateChanged, User } from "firebase/auth";
import { createLogger } from "../utils/logger";
import { apiCall } from "./firebase";

const logger = createLogger("PluginService");

export class PluginService {
  private pollingInterval: NodeJS.Timeout | null = null;
  private isPolling = false;
  private auth = getAuth();
  private isInitialized = false;

  /**
   * Initialize the service and start listening for auth state changes
   */
  init(): void {
    if (this.isInitialized) {
      return;
    }

    logger.debug("Initializing...");

    // Check if user is already authenticated
    const currentUser = this.auth.currentUser;
    if (currentUser) {
      logger.debug(`User already authenticated - ${currentUser.email}`);
      this.startPolling();
    }

    onAuthStateChanged(this.auth, (user) => {
      if (user) {
        this.onUserLogin(user);
      } else {
        this.onUserLogout();
      }
    });

    this.isInitialized = true;
  }

  /**
   * Handle user login
   */
  private onUserLogin(user: User): void {
    logger.debug(`User logged in - ${user.email}`);
    this.startPolling();
  }

  /**
   * Handle user logout
   */
  private onUserLogout(): void {
    logger.debug("User logged out");
    this.stopPolling();
  }

  /**
   * Start polling for plugin updates
   */
  private startPolling(): void {
    if (this.isPolling) {
      logger.debug("Already polling, skipping start");
      return;
    }

    logger.debug("Starting polling...");
    this.isPolling = true;

    // Poll immediately
    this.pollForUpdates();

    // Then poll every 5 minutes
    this.pollingInterval = setInterval(
      () => {
        this.pollForUpdates();
      },
      5 * 60 * 1000
    );
  }

  /**
   * Stop polling for plugin updates
   */
  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
    logger.debug("Polling stopped");
  }

  /**
   * Poll for plugin updates
   */
  private async pollForUpdates(): Promise<void> {
    try {
      logger.debug("Polling for updates...");

      // Get current user
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        logger.debug("No authenticated user, skipping poll");
        return;
      }

      // Call the polling endpoint
      await apiCall("/pollForUser", {
        method: "POST",
        body: JSON.stringify({ userId: currentUser.uid }),
      });

      logger.debug("Poll completed successfully");
    } catch (error) {
      logger.error("Error during polling:", error);
    }
  }

  /**
   * Poll for current user (debug function)
   */
  async pollForCurrentUser(): Promise<void> {
    try {
      logger.debug("Polling for current user...");

      // Get current user
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        throw new Error("No authenticated user");
      }

      // Call the polling endpoint
      await apiCall("/pollForUser", {
        method: "POST",
        body: JSON.stringify({ userId: currentUser.uid }),
      });

      logger.debug("Poll for current user completed successfully");
    } catch (error) {
      logger.error("Error polling for current user:", error);
      throw error;
    }
  }

  /**
   * Get plugin information (events and actions)
   */
  async getPluginInfo(): Promise<any> {
    try {
      logger.debug("Getting plugin info...");
      const response = await apiCall("/getPluginInfo");
      logger.debug("Plugin info retrieved:", response.plugins);
      return response;
    } catch (error) {
      logger.error("Failed to get plugin info:", error);
      throw error;
    }
  }
}

// Create singleton instance
export const pluginService = new PluginService();
